import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'currentHrv', 'baselineHrv', 'age', 'timing',
    'baselineDisplay', 'baselineSource', 'percentChange',
    'statusContainer', 'statusDot', 'statusText', 'statusDescription',
    'scoreBar', 'scoreValue', 'recommendations'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const currentHrv = parseFloat(this.currentHrvTarget.value) || 45
    const inputBaseline = parseFloat(this.baselineHrvTarget.value)
    const age = parseInt(this.ageTarget.value, 10) || 30
    const timing = this.timingTargets.find((r) => r.checked)?.value || 'morning'

    // Calculate baseline - use input if provided, otherwise estimate from age
    let baseline
    let baselineSource

    if (inputBaseline && inputBaseline > 0) {
      baseline = inputBaseline
      baselineSource = 'Personal baseline'
    } else {
      baseline = this.estimateBaselineFromAge(age)
      baselineSource = 'Age-based estimate'
    }

    this.baselineDisplayTarget.textContent = Math.round(baseline)
    this.baselineSourceTarget.textContent = baselineSource

    // Calculate percent change from baseline
    const percentChange = ((currentHrv - baseline) / baseline) * 100
    const percentSign = percentChange >= 0 ? '+' : ''
    this.percentChangeTarget.textContent = `${percentSign}${percentChange.toFixed(1)}`

    // Calculate readiness score (0-100)
    const readinessScore = this.calculateReadinessScore(percentChange, timing)
    this.scoreValueTarget.textContent = readinessScore

    // Update score bar
    const barColor = this.getScoreColor(readinessScore)
    this.scoreBarTarget.style.width = `${readinessScore}%`
    this.scoreBarTarget.style.backgroundColor = barColor

    // Update status based on percent change
    this.updateStatus(percentChange, timing)
    this.updateRecommendations(percentChange, readinessScore, timing)
  }

  estimateBaselineFromAge(age) {
    // Age-based typical HRV ranges (midpoint of typical range)
    // These are approximate median RMSSD values by age group
    if (age <= 25) {
      return 80 - (age - 18) * 1.5 // ~80ms at 18, declining
    } if (age <= 35) {
      return 67 - (age - 25) * 1.5 // ~67ms at 25
    } if (age <= 45) {
      return 55 - (age - 35) * 1.2 // ~55ms at 35
    } if (age <= 55) {
      return 43 - (age - 45) * 0.8 // ~43ms at 45
    } if (age <= 65) {
      return 35 - (age - 55) * 0.5 // ~35ms at 55
    }
    return Math.max(20, 30 - (age - 65) * 0.3) // ~30ms at 65, floor at 20
  }

  calculateReadinessScore(percentChange, timing) {
    // Convert percent change to a 0-100 score
    // +20% or more = 100, -20% or less = 20
    let score = 60 + (percentChange * 2)

    // Cap score between 20 and 100
    score = Math.min(100, Math.max(20, score))

    // Reduce confidence slightly if not morning measurement
    if (timing !== 'morning') {
      score = Math.round(score * 0.95)
    }

    return Math.round(score)
  }

  getScoreColor(score) {
    if (score >= 75) {
      return '#22c55e' // green-500
    } if (score >= 50) {
      return '#eab308' // yellow-500
    }
    return '#ef4444' // red-500
  }

  updateStatus(percentChange, timing) {
    let dotColor; let statusText; let description

    if (percentChange >= 10) {
      // Well recovered
      dotColor = 'bg-green-500'
      statusText = 'WELL RECOVERED'
      description = 'Your HRV is significantly above baseline. Your nervous system is well-recovered and ready for high-intensity training.'
    } else if (percentChange >= -10) {
      // Normal
      dotColor = 'bg-yellow-500'
      statusText = 'NORMAL READINESS'
      description = 'Your HRV is within normal range of your baseline. Proceed with planned training while listening to your body.'
    } else {
      // Needs recovery
      dotColor = 'bg-red-500'
      statusText = 'NEEDS RECOVERY'
      description = 'Your HRV is below baseline, indicating incomplete recovery. Consider lighter training or active recovery today.'
    }

    // Add timing caveat if not morning
    if (timing !== 'morning') {
      description += ' Note: Measurements taken outside of morning may be less reliable.'
    }

    this.statusDotTarget.className = `w-4 h-4 ${dotColor}`
    this.statusTextTarget.textContent = statusText
    this.statusDescriptionTarget.textContent = description
  }

  updateRecommendations(percentChange, score, timing) {
    let recommendations = []

    if (percentChange >= 10) {
      // Well recovered
      recommendations = [
        'Excellent day for high-intensity training or competition',
        'Push for new personal records in strength or speed',
        'High-intensity intervals will be well-tolerated',
        'Skill work requiring focus and coordination will be effective'
      ]
    } else if (percentChange >= 0) {
      // Normal-high
      recommendations = [
        'Good day for moderate to high intensity training',
        'Regular strength training appropriate',
        'Zone 2-3 cardio work recommended',
        'Monitor fatigue during workout and adjust if needed'
      ]
    } else if (percentChange >= -10) {
      // Normal-low
      recommendations = [
        'Moderate training intensity recommended',
        'Focus on technique and form over intensity',
        'Keep strength work at 70-80% of max',
        'Consider reducing volume slightly'
      ]
    } else if (percentChange >= -20) {
      // Needs recovery
      recommendations = [
        'Light training only - active recovery day',
        'Walking, yoga, or gentle mobility work',
        'Skip high-intensity intervals today',
        'Focus on sleep and nutrition for recovery'
      ]
    } else {
      // Significantly depleted
      recommendations = [
        'Rest day strongly recommended',
        'Light stretching or walking only if desired',
        'Prioritize 8+ hours of sleep tonight',
        'Consider factors: illness, stress, poor sleep, overtraining'
      ]
    }

    // Add timing-specific recommendation
    if (timing !== 'morning') {
      recommendations.push('For more accurate readings, measure within 5 minutes of waking')
    }

    this.recommendationsTarget.innerHTML = recommendations
      .map((rec) => `<li class="flex items-start gap-2"><span class="text-gray-400">></span><span>${rec}</span></li>`)
      .join('')
  }
}
